/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @stopgap — ADDRESS THE BRAND IMAGE CDN BY ITS CANONICAL CLOUDFRONT NAME.
 *
 * Same objects, same distribution, same Cloudinary transforms — a different
 * hostname. Nothing about request volume, caching or origin load changes; only
 * the name in the URL.
 *
 * THE PROBLEM THIS SOLVES. On the corporate VPN, the internal resolver rewrites
 * the brand image hostnames into carrier-grade NAT space:
 *
 *     VPN on   media.neimanmarcus.com  ->  100.64.0.37          (synthesized A record,
 *                                                                no CNAME chain)
 *     VPN off  media.neimanmarcus.com  ->  cdn.neimanmarcus.com
 *                                      ->  d2zuh0aaxtlvpo.cloudfront.net
 *                                      ->  143.204.142.x        (public)
 *
 * The storefront itself is on `*.commercecloud.salesforce.com`, which resolves
 * publicly on the same machine. So a page in PUBLIC address space requests an
 * image in PRIVATE address space, and Chrome's Local Network Access check gates
 * it: the user gets a "access other devices on your local network" prompt, and
 * blocking it leaves a product grid with no product images. DevTools reports the
 * failures as `CORS error` because PNA is implemented as a CORS preflight, which
 * is what made this so hard to read.
 *
 * Every other Saks storefront is served from a brand domain, so its page is
 * rewritten into the same private space as its images — private to private, no
 * mismatch, no gate. Ours is the only one with a foot in each space.
 *
 * WHY THE CANONICAL NAME WORKS. The resolver's rewrite is scoped to the brand
 * hostnames. Nobody writes split-horizon rules for AWS canonical distribution
 * names, so `d2zuh0aaxtlvpo.cloudfront.net` passes through untouched. Verified
 * on the affected machine, on the VPN:
 *
 *     dig +short d2zuh0aaxtlvpo.cloudfront.net
 *     143.204.142.39 / .112 / .114 / .79      <- public, identical to the
 *                                                off-VPN answer for media.*
 *
 * And the objects are byte-identical across both names: 8 of 8 real product
 * images returned the same status and the same content length on each, plus a
 * BG object at 276,364 bytes on both of its names.
 *
 * WHAT RETIRES IT — any one of these:
 *   1. The storefront gets a brand-domain alias, putting it in the same address
 *      space as the images. This is the real fix and it removes the mismatch at
 *      the source rather than routing around it.
 *   2. The corporate resolver stops rewriting the brand image hostnames.
 *   3. The catalog serves image URLs from a host that was never rewritten.
 *
 * WHAT NOT TO DO. Do not add hostnames here to solve an unrelated image problem.
 * This map exists for exactly one reason — hostnames the corporate resolver
 * rewrites — and every entry needs a `dig` on the affected network proving the
 * canonical name is NOT rewritten. Without that evidence an entry is a guess
 * that silently changes where a brand's imagery comes from.
 *
 * KNOWN FRAGILITY, stated so nobody is surprised: a canonical CloudFront name is
 * not a contract. Saks can move the distribution and AWS can change the name,
 * and either would break every product image at once with no warning. That is
 * acceptable for a demo and is NOT acceptable for production — which is what
 * retirement condition 1 is for.
 */

/**
 * Brand image hostnames -> the canonical CloudFront name of the same
 * distribution.
 *
 * Both `media.*` and `cdn.*` are listed per brand because `media.*` CNAMEs
 * through `cdn.*` to one distribution, and both names appear in catalog data.
 */
const CANONICAL_CDN_HOST: Readonly<Record<string, string>> = {
    'media.neimanmarcus.com': 'd2zuh0aaxtlvpo.cloudfront.net',
    'cdn.neimanmarcus.com': 'd2zuh0aaxtlvpo.cloudfront.net',
    'media.bergdorfgoodman.com': 'd14ffjdh2tf6td.cloudfront.net',
    'cdn.bergdorfgoodman.com': 'd14ffjdh2tf6td.cloudfront.net',
    /* Saks Global's Scene7 host, which arrives in SCAPI catalogue data rather than being a host we
     * choose -- some masters on this sandbox carry `https://image.s5a.com/is/image/saks/<id>`.
     *
     * It answers 301 to cdn.saksfifthavenue.com on every request, so rewriting at source removes a
     * redirect per image AND stops the browser ever contacting the alias -- which is the half that
     * matters, because that alias is the one a corporate resolver answers with a private-space
     * address, gating the fetch as private-network access and painting nothing.
     *
     * The CSP already allowlisted BOTH names, with a comment explaining the redirect. So the
     * redirect was known and handled at the policy layer while the URL itself was never rewritten.
     *
     * Verified against the PDP's own image set rather than one sample: all 6 distinct Scene7 URLs
     * in that page's fetchable attributes return 200 from the canonical host, 272KB-1.7MB each. */
    'image.s5a.com': 'cdn.saksfifthavenue.com',
};

/**
 * The canonical CloudFront hostnames, for the CSP `img-src` allowlist.
 *
 * Exported so `config.server.ts` cannot drift from this map — the allowlist is
 * derived from the same source as the rewrite, so a host we rewrite to is a host
 * the page is permitted to load. Getting these out of step would block every
 * product image with a CSP violation instead of a network error, which looks
 * like a different bug entirely.
 */
export const CANONICAL_CDN_ORIGINS: readonly string[] = [
    ...new Set(Object.values(CANONICAL_CDN_HOST).map((host) => `https://${host}`)),
];

/**
 * Rewrite a brand-CDN image URL to its canonical CloudFront hostname.
 *
 * Anything else — the content library, DIS, relative static paths, data URIs,
 * an already-canonical URL — is returned untouched. Only the hostname changes;
 * path, query and Cloudinary transform segment are preserved exactly.
 */
export function toCanonicalCdnUrl(src: string | undefined): string | undefined {
    if (!src) {
        return src;
    }

    let url: URL;
    try {
        url = new URL(src);
    } catch {
        // Relative paths and data URIs land here and must pass through.
        return src;
    }

    const canonical = CANONICAL_CDN_HOST[url.hostname.toLowerCase()];
    if (!canonical) {
        return src;
    }

    url.hostname = canonical;
    return url.toString();
}
